import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { FormContext } from './context'

import styles from './index.module.css'

import Checkbox from './Checkbox'
import Label from './Label'
import Input from './Input'
import ClearButton from './ClearButton'
import SubmitButton from './SubmitButton'
import Fieldset from './Fieldset'
import Legend from './Legend'
import Separator from './Separator'
import validations, { type Rule } from './Validations'
import Collapsible from './Collapsible'

export type FormValueTypes = string | boolean | number | string[]
export type FormValues<T extends Record<string, FormValueTypes> = Record<string, FormValueTypes>> =
  T
export type FormMetadata = { submitted: boolean }

interface IFormProps<T extends Record<string, FormValueTypes> = Record<string, FormValueTypes>> {
  defaultValue?: FormValues<T>
  onSubmit?: (form: FormValues<T>) => void
  onChange?: (form: FormValues<T>) => void
  children: React.ReactNode
  debounceOnChange?: number
}

const Form = <T extends Record<string, FormValueTypes> = Record<string, FormValueTypes>>({
  onSubmit,
  onChange,
  defaultValue,
  children,
  debounceOnChange = 0,
}: IFormProps<T>) => {
  const [form, setForm] = useState<FormValues<T>>(defaultValue ?? ({} as FormValues<T>))
  const [formValidations, setFormValidations] = useState<Record<string, Array<ReturnType<Rule>>>>(
    {}
  )
  const [errors, setErrors] = useState<Record<string, string>>({})

  const [formMetaData, setFormMetaData] = useState<FormMetadata>({
    submitted: false,
  })

  const [hasFormInitialized, setHasFormInitialized] = useState<Record<string, boolean>>({})

  const isUserChangeRef = useRef(false)

  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  const setFormValue = useCallback(
    (key: string, value: string | boolean | number | string[], isUserAction: boolean) => {
      isUserChangeRef.current = isUserAction
      setForm(prev => ({ ...prev, [key]: value }) as FormValues<T>)
    },
    []
  )

  const setFormArrayValue = useCallback(
    (key: string, value: string | boolean | undefined, isUserAction: boolean) => {
      isUserChangeRef.current = isUserAction

      setForm(prev => {
        const prevValue = prev[key]
        if (Array.isArray(prevValue)) {
          return {
            ...prev,
            [key]: [...prevValue, value as string].filter(Boolean),
          } as FormValues<T>
        }
        return { ...prev, [key]: [value as string].filter(Boolean) } as FormValues<T>
      })
    },
    []
  )

  const removeFormArrayValue = useCallback(
    (key: string, value: string | boolean, isUserAction: boolean) => {
      isUserChangeRef.current = isUserAction

      setForm(prev => {
        const prevValue = prev[key]
        if (Array.isArray(prevValue)) {
          return { ...prev, [key]: prevValue.filter(item => item !== value) } as FormValues<T>
        }
        return { ...prev, [key]: [] } as FormValues<T>
      })
    },
    []
  )

  const deleteFormValue = useCallback((key: string, isUserAction: boolean) => {
    isUserChangeRef.current = isUserAction
    setForm(prev => {
      const { [key]: _, ...rest } = prev

      return rest as FormValues<T>
    })

    setErrors(prev => {
      const { [key]: _, ...rest } = prev

      return rest
    })
  }, [])

  const setFormValidation = useCallback(
    (key: string, inputValidations: Array<ReturnType<Rule>>) => {
      setFormValidations(prev => ({ ...prev, [key]: inputValidations }))
    },
    []
  )

  const validateForm = () => {
    const { isValid, errors: newErrors } = Object.entries(formValidations).reduce(
      (acc, [key, inputValidations]) => {
        const value = form[key]

        const failedResult = inputValidations
          .map(validation => validation(value))
          .find(result => !result.isValid)

        if (failedResult && failedResult.isValid === false) {
          acc.errors[key] = failedResult.message
          acc.isValid = false
        }

        return acc
      },
      { isValid: true, errors: {} as Record<string, string> }
    )

    setErrors(newErrors)

    return isValid
  }

  const setFormMetaDataInner = useCallback((metaData: FormMetadata) => {
    setFormMetaData(prev => ({ ...prev, ...metaData }))
  }, [])

  useEffect(() => {
    if (!isUserChangeRef.current) return undefined

    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current)
    }

    debounceTimeoutRef.current = setTimeout(() => {
      if (formMetaData.submitted) {
        validateForm()
      }

      onChange?.(form)
    }, debounceOnChange)

    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current)
      }
    }
  }, [form]) // eslint-disable-line react-hooks/exhaustive-deps

  const setInputInitialized = useCallback((name: string, isInitialized: boolean) => {
    setHasFormInitialized(prev => ({ ...prev, [name]: isInitialized }))
  }, [])

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setErrors({})
    setFormMetaData(prev => ({ ...prev, submitted: true }))

    if (!validateForm()) return
    onSubmit?.(form)
  }

  const contextValue = useMemo(
    () => ({
      form,
      errors,
      setFormValue,
      setFormArrayValue,
      removeFormArrayValue,
      deleteFormValue,
      setFormValidation,
      setFormMetaData: setFormMetaDataInner,
      setInputInitialized,
      hasFormInitialized: Object.values(hasFormInitialized).every(Boolean),
    }),
    [
      form,
      errors,
      setFormValue,
      setFormArrayValue,
      removeFormArrayValue,
      deleteFormValue,
      setFormValidation,
      setFormMetaDataInner,
      setInputInitialized,
      hasFormInitialized,
    ]
  )

  return (
    <FormContext.Provider value={contextValue}>
      <form onSubmit={handleSubmit} className={styles.form}>
        {children}
      </form>
    </FormContext.Provider>
  )
}

Form.Checkbox = Checkbox
Form.Label = Label
Form.Input = Input
Form.ClearButton = ClearButton
Form.SubmitButton = SubmitButton
Form.Fieldset = Fieldset
Form.Legend = Legend
Form.Separator = Separator
Form.Collapsible = Collapsible

Form.Validations = validations

Form.displayName = 'Form'
export default Form
